import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { getHttpErrorStatus } from '../../utils/error.utils';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  AlertFeedLane,
  ClientStatsAlertsResponse,
  ClientStatsPortfolioParams,
  ClientStatsPortfolioResponse,
  ClientStatsPreset,
  ClientStatsSeriesResponse,
  IClientStatsService,
} from './client-stats.service.interface';

@Injectable()
export class ClientStatsService implements IClientStatsService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  async getPortfolio(params: ClientStatsPortfolioParams): Promise<ClientStatsPortfolioResponse> {
    const search = new URLSearchParams();
    search.set('preset', params.preset);
    search.set('page', String(params.page));
    search.set('pageSize', String(params.pageSize));
    const term = params.search?.trim();
    if (term) {
      search.set('search', term);
    }
    if (params.sortBy) {
      search.set('sortBy', params.sortBy);
    }
    if (params.sortOrder) {
      search.set('sortOrder', params.sortOrder);
    }
    return this.http.get<ClientStatsPortfolioResponse>(
      `consultant/clients/stats?${search.toString()}`,
    );
  }

  async getAlerts(
    preset: ClientStatsPreset,
    lane: AlertFeedLane = 'operational',
  ): Promise<ClientStatsAlertsResponse> {
    const search = new URLSearchParams();
    search.set('preset', preset);
    // Default non serializzato (pattern URL della pagina): operational è la
    // corsia di default anche lato backend.
    if (lane && lane !== 'operational') {
      search.set('lane', lane);
    }
    return this.http.get<ClientStatsAlertsResponse>(
      `consultant/clients/stats/alerts?${search.toString()}`,
    );
  }

  async getSeries(
    tenantId: string,
    preset: ClientStatsPreset,
  ): Promise<ClientStatsSeriesResponse | null> {
    const search = new URLSearchParams();
    search.set('preset', preset);
    try {
      return await this.http.get<ClientStatsSeriesResponse>(
        `consultant/clients/stats/${tenantId}/series?${search.toString()}`,
      );
    } catch (err) {
      // 404 uniforme del contratto (tenant fuori perimetro o inesistente):
      // stato vuoto già previsto dalla pagina di dettaglio (`series === null`),
      // non un errore da ritentare. Bug verificato: senza questo catch il 404
      // restava un errore, TanStack lo ritentava, e la pagina mostrava
      // "errore, riprova" al posto dell'empty state.
      const status = getHttpErrorStatus(err);
      if (status === 404) {
        return null;
      }
      throw err;
    }
  }

  async getOwnSeries(preset: ClientStatsPreset): Promise<ClientStatsSeriesResponse> {
    const search = new URLSearchParams();
    search.set('preset', preset);
    return this.http.get<ClientStatsSeriesResponse>(`haccp/stats/series?${search.toString()}`);
  }
}
